// ---------- un pseudo au hasard ----------
// Le dé du dialogue du pseudo (#usernameDialog) : un nom gentil, un peu drôle, pris dans le monde
// du jeu — ce qui colorie (pinceau, gomme, palette), ce qu'on y trouve (tache, pixel, points) et
// des bêtes qui s'y essaient. « Pinceau qui déborde », « Loutre pointilliste », « Dotty Otter ».
//
// UNE LISTE PAR LANGUE, ET PAS UNE TRADUCTION. Un jeu de mots ne traverse pas la frontière
// (« Dotty » dit à la fois « à pois » et « un peu toqué », rien ne le rend en français), et
// l'ordre change : le nom d'abord en français, espagnol et portugais, l'adjectif d'abord en
// anglais et en allemand. Les adjectifs S'ACCORDENT : chaque nom porte son genre, chaque
// adjectif ses formes — « Gomme rêveuse », « Crayon rêveur » ; en allemand, la terminaison forte
// du nominatif sans article (« verträumter Igel », « verträumte Eule », « verträumtes Lama »).
//
// CE QUE LE SERVEUR ACCEPTE. `validUsername` (backend/src/validation.ts) : une lettre, puis
// lettres ASCII, chiffres ou `_`, 20 au plus. On n'y touche pas — les anciens APK portent le même
// contrôle (src/prefs.ts) et doivent continuer d'accepter tout ce qu'un nouveau propose. Les mots
// s'écrivent donc ici avec leurs accents, pour rester lisibles, et sont pliés en ASCII au moment
// de les coller : « Hérisson » → `Herisson`, « verträumt » → `Vertraeumt` (l'allemand écrit ae,
// oe, ue quand l'umlaut manque ; ôter seulement le point en ferait un autre mot).
//
// DEUX CHIFFRES AU BOUT. Quelques centaines de combinaisons par langue, un pseudo unique par
// compte : sans eux, le dé finirait par tomber sur un nom pris et le joueur lirait « déjà pris »
// pour un nom qu'il n'a pas choisi.

import type { Lang } from './i18n';

type Gender = 'm' | 'f' | 'n';

interface Noun {
  w: string;
  g: Gender;
}

/** Un adjectif : une forme par genre. Une seule chaîne quand il ne varie pas (« Zen »,
 *  « qui déborde ») ; en allemand, le radical, la terminaison vient du genre. */
type Adjective = string | Partial<Record<Gender, string>>;

interface Words {
  order: 'noun-adj' | 'adj-noun';
  nouns: Noun[];
  adjectives: Adjective[];
}

const m = (w: string): Noun => ({ w, g: 'm' });
const f = (w: string): Noun => ({ w, g: 'f' });
const n = (w: string): Noun => ({ w, g: 'n' });

/** `Record<Lang, …>` : une langue ajoutée au jeu sans sa liste ne compile pas. */
const WORDS: Record<Lang, Words> = {
  fr: {
    order: 'noun-adj',
    nouns: [
      m('Pinceau'),
      m('Crayon'),
      f('Gomme'),
      f('Palette'),
      m('Pastel'),
      m('Pixel'),
      f('Tache'),
      m('Hibou'),
      f('Loutre'),
      m('Panda'),
      m('Lama'),
      m('Pingouin'),
      f('Licorne'),
      m('Escargot'),
      f('Chouette'),
      m('Hérisson'),
      m('Poulpe'),
      m('Colibri'),
    ],
    adjectives: [
      { m: 'Rêveur', f: 'Rêveuse' },
      { m: 'Distrait', f: 'Distraite' },
      'Pointilliste',
      { m: 'Barbouillé', f: 'Barbouillée' },
      'Timide',
      { m: 'Gourmand', f: 'Gourmande' },
      'Zen',
      { m: 'Joyeux', f: 'Joyeuse' },
      { m: 'Étourdi', f: 'Étourdie' },
      { m: 'Tacheté', f: 'Tachetée' },
      { m: 'Farceur', f: 'Farceuse' },
      { m: 'Appliqué', f: 'Appliquée' },
      'Multicolore',
      { m: 'Ébouriffé', f: 'Ébouriffée' },
      { m: 'Méticuleux', f: 'Méticuleuse' },
      // Le défaut que tout le monde a en coloriant, et que ce jeu ne permet pas.
      'Qui déborde',
      'Arc-en-ciel',
    ],
  },
  en: {
    order: 'adj-noun',
    nouns: [
      // Pas « Brush » : avec le suffixe 17, la modération lit « brushit » (elle replie 1 → i,
      // 7 → t) et refusait un nom que le dé venait de proposer. Le chevalet reste dans le thème.
      m('Easel'),
      m('Crayon'),
      m('Eraser'),
      m('Palette'),
      m('Pastel'),
      m('Pixel'),
      m('Splodge'),
      m('Owl'),
      m('Otter'),
      m('Panda'),
      m('Llama'),
      m('Penguin'),
      m('Unicorn'),
      m('Snail'),
      m('Hedgehog'),
      m('Octopus'),
      m('Doodle'),
    ],
    adjectives: [
      'Dreamy',
      // À pois, et un peu toqué : le pointillisme en un mot.
      'Dotty',
      'Smudgy',
      'Shy',
      'Hungry',
      'Zen',
      'Cheerful',
      'Sleepy',
      'Speckled',
      'Cheeky',
      'Messy',
      'Fussy',
      'Tidy',
      'Fluffy',
      'Wobbly',
      'Rainbow',
      'Curious',
    ],
  },
  es: {
    order: 'noun-adj',
    nouns: [
      m('Pincel'),
      m('Lápiz'),
      f('Goma'),
      f('Paleta'),
      m('Pastel'),
      m('Píxel'),
      f('Mancha'),
      m('Búho'),
      f('Nutria'),
      m('Panda'),
      f('Llama'),
      m('Pingüino'),
      m('Unicornio'),
      m('Caracol'),
      f('Lechuza'),
      m('Erizo'),
      m('Pulpo'),
    ],
    // Pas de ñ : plié en n, « soñador » deviendrait « sonador », qui ne veut plus rien dire.
    adjectives: [
      { m: 'Curioso', f: 'Curiosa' },
      { m: 'Despistado', f: 'Despistada' },
      'Puntillista',
      { m: 'Manchado', f: 'Manchada' },
      { m: 'Tímido', f: 'Tímida' },
      { m: 'Goloso', f: 'Golosa' },
      'Zen',
      'Alegre',
      { m: 'Dormilón', f: 'Dormilona' },
      { m: 'Moteado', f: 'Moteada' },
      'Bromista',
      { m: 'Pícaro', f: 'Pícara' },
      { m: 'Aplicado', f: 'Aplicada' },
      'Multicolor',
      { m: 'Despeinado', f: 'Despeinada' },
      { m: 'Meticuloso', f: 'Meticulosa' },
    ],
  },
  pt: {
    order: 'noun-adj',
    nouns: [
      m('Pincel'),
      m('Lápis'),
      f('Borracha'),
      f('Paleta'),
      m('Pastel'),
      m('Pixel'),
      f('Mancha'),
      f('Coruja'),
      f('Lontra'),
      m('Panda'),
      f('Lhama'),
      m('Pinguim'),
      m('Unicórnio'),
      m('Caracol'),
      m('Polvo'),
      m('Tucano'),
    ],
    adjectives: [
      { m: 'Sonhador', f: 'Sonhadora' },
      { m: 'Distraído', f: 'Distraída' },
      'Pontilhista',
      { m: 'Manchado', f: 'Manchada' },
      { m: 'Tímido', f: 'Tímida' },
      { m: 'Guloso', f: 'Gulosa' },
      'Zen',
      'Alegre',
      { m: 'Dorminhoco', f: 'Dorminhoca' },
      { m: 'Pintado', f: 'Pintada' },
      { m: 'Brincalhão', f: 'Brincalhona' },
      { m: 'Travesso', f: 'Travessa' },
      // « Caprichado » au Brésil : fait avec soin — ce qu'on dit d'un coloriage réussi.
      { m: 'Caprichoso', f: 'Caprichosa' },
      { m: 'Colorido', f: 'Colorida' },
      { m: 'Descabelado', f: 'Descabelada' },
      { m: 'Curioso', f: 'Curiosa' },
    ],
  },
  de: {
    order: 'adj-noun',
    nouns: [
      m('Pinsel'),
      m('Stift'),
      m('Radiergummi'),
      f('Palette'),
      m('Pixel'),
      m('Klecks'),
      f('Eule'),
      m('Otter'),
      m('Panda'),
      n('Lama'),
      m('Pinguin'),
      n('Einhorn'),
      f('Schnecke'),
      m('Igel'),
      m('Krake'),
      m('Kolibri'),
      m('Farbtopf'),
      m('Dachs'),
      m('Hase'),
      f('Kreide'),
    ],
    // Des radicaux : la terminaison forte du nominatif s'ajoute selon le genre (DE_ENDINGS).
    adjectives: [
      'verträumt',
      'zerstreut',
      'gepunktet',
      'verschmiert',
      'schüchtern',
      'naschhaft',
      'fröhlich',
      'verschlafen',
      'gefleckt',
      'frech',
      'kunterbunt',
      'pingelig',
      'flauschig',
      'wuschelig',
      'neugierig',
      'munter',
    ],
  },
};

const DE_ENDINGS: Record<Gender, string> = { m: 'er', f: 'e', n: 'es' };

/** La place que laissent les deux chiffres sous les 20 caractères du serveur. */
const MAX_BASE = 18;

/** En ASCII, chaque mot avec sa majuscule : « qui déborde » → `QuiDeborde`, « Arc-en-ciel » →
 *  `ArcEnCiel`. L'umlaut ne devient ae, oe, ue qu'en ALLEMAND : le tréma espagnol de « pingüino »
 *  se retire simplement — `Pingueino` n'est un mot nulle part (le test l'a attrapé). */
export function fold(s: string, lang: Lang): string {
  const t =
    lang === 'de'
      ? s
          .replace(/ä/g, 'ae')
          .replace(/ö/g, 'oe')
          .replace(/ü/g, 'ue')
          .replace(/Ä/g, 'Ae')
          .replace(/Ö/g, 'Oe')
          .replace(/Ü/g, 'Ue')
          .replace(/ß/g, 'ss')
      : s;
  return t
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^A-Za-z]+/)
    .filter(Boolean)
    .map((p) => p[0].toUpperCase() + p.slice(1))
    .join('');
}

function agree(lang: Lang, adj: Adjective, g: Gender): string {
  if (lang === 'de' && typeof adj === 'string') return adj + DE_ENDINGS[g];
  if (typeof adj === 'string') return adj;
  // Les langues sans neutre ici n'en ont pas besoin ; le masculin sert de repli.
  return adj[g] ?? adj.m ?? '';
}

function base(lang: Lang, noun: Noun, adj: Adjective): string {
  const a = agree(lang, adj, noun.g);
  return fold(WORDS[lang].order === 'noun-adj' ? `${noun.w} ${a}` : `${a} ${noun.w}`, lang);
}

/** Toutes les combinaisons d'une langue qui tiennent, sans les chiffres — pour les tests, qui les
 *  passent au filtre de modération du serveur. */
export function allBases(lang: Lang): string[] {
  const out: string[] = [];
  for (const noun of WORDS[lang].nouns) {
    for (const adj of WORDS[lang].adjectives) {
      const b = base(lang, noun, adj);
      if (b.length <= MAX_BASE) out.push(b);
    }
  }
  return out;
}

/** Un pseudo au hasard dans la langue du joueur. `rand` pour les tests. */
export function randomUsername(lang: Lang, rand: () => number = Math.random): string {
  const pick = <T>(list: readonly T[]): T => list[Math.floor(rand() * list.length)];
  const words = WORDS[lang];
  let b = '';
  // Une paire trop longue (« Radiergummi » avec « verschlafen ») est retirée ; quelques essais
  // suffisent toujours, le repli n'est là que pour qu'une boucle ne puisse pas tourner sans fin.
  for (let i = 0; i < 20 && !b; i++) {
    const candidate = base(lang, pick(words.nouns), pick(words.adjectives));
    if (candidate.length <= MAX_BASE) b = candidate;
  }
  if (!b) b = allBases(lang)[0];
  return b + String(10 + Math.floor(rand() * 90));
}
